import { RecordingStatus, } from '@components/AudioRecorder/types';
import { useCallback, useEffect, useRef, useState } from 'react';
export const useAudioRecorder = (onRecordingComplete, onError, maxRecordingDuration = 10) => {
    const [status, setStatus] = useState(RecordingStatus.IDLE);
    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);
    const streamRef = useRef(null);
    const isResettingRef = useRef(false);
    // Duration tracking
    const recordingStartTimeRef = useRef(null);
    // Store callbacks in refs to avoid stale closures
    const onRecordingCompleteRef = useRef(onRecordingComplete);
    const onErrorRef = useRef(onError);
    // Keep callback refs updated using useEffect
    useEffect(() => {
        onRecordingCompleteRef.current = onRecordingComplete;
    }, [onRecordingComplete]);
    useEffect(() => {
        onErrorRef.current = onError;
    }, [onError]);
    /**
     * Centralized cleanup function - single source of truth
     * Handles all resource cleanup: streams, chunks
     */
    const cleanupResources = useCallback(() => {
        // Stop and release microphone tracks
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        // Clear audio chunks (blob data will be garbage collected)
        chunksRef.current = [];
        // Clear duration tracking
        recordingStartTimeRef.current = null;
    }, []);
    const startRecording = useCallback(async () => {
        try {
            setStatus(RecordingStatus.REQUESTING_PERMISSION);
            isResettingRef.current = false;
            // Request microphone access
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    sampleRate: 44100,
                    channelCount: 1, // Mono for smaller file size
                },
            });
            streamRef.current = stream;
            // Create MediaRecorder instance
            const mediaRecorder = new MediaRecorder(stream, {
                mimeType: MediaRecorder.isTypeSupported('audio/webm')
                    ? 'audio/webm'
                    : 'audio/ogg',
            });
            mediaRecorderRef.current = mediaRecorder;
            chunksRef.current = [];
            // Handle data available
            mediaRecorder.ondataavailable = event => {
                if (event.data.size > 0) {
                    chunksRef.current.push(event.data);
                }
            };
            // Handle recording start
            mediaRecorder.onstart = () => {
                // Record start time
                recordingStartTimeRef.current = Date.now();
            };
            // Handle recording stop
            mediaRecorder.onstop = () => {
                // Early return if this was triggered by reset
                if (isResettingRef.current) {
                    cleanupResources();
                    return;
                }
                // Calculate duration
                const duration = recordingStartTimeRef.current
                    ? (Date.now() - recordingStartTimeRef.current) / 1000
                    : 0;
                // Validate duration
                if (duration > maxRecordingDuration) {
                    // Recording too long - reject it
                    const errorMsg = `Recording exceeded maximum duration of ${maxRecordingDuration}s`;
                    setStatus(RecordingStatus.IDLE);
                    // Notify error callback
                    onErrorRef.current?.(errorMsg);
                    // Cleanup without creating blob
                    // Chunks will be garbage collected
                    cleanupResources();
                    return;
                }
                // Valid recording - create blob
                const blob = new Blob(chunksRef.current, {
                    type: mediaRecorder.mimeType,
                });
                setStatus(RecordingStatus.STOPPED);
                // Call completion callback
                onRecordingCompleteRef.current?.(blob);
                // Cleanup resources
                cleanupResources();
            };
            // Handle errors
            mediaRecorder.onerror = (event) => {
                const errorEvent = event;
                const errorMessage = errorEvent.message || 'Recording error occurred';
                setStatus(RecordingStatus.ERROR);
                // Call error callback
                onErrorRef.current?.(errorMessage);
                // Cleanup resources
                cleanupResources();
            };
            // Start recording
            mediaRecorder.start();
            setStatus(RecordingStatus.RECORDING);
        }
        catch (err) {
            let errorMessage = 'Failed to start recording';
            if (err instanceof Error) {
                if (err.name === 'NotAllowedError') {
                    errorMessage =
                        'Microphone permission denied. Please allow microphone access.';
                }
                else if (err.name === 'NotFoundError') {
                    errorMessage = 'No microphone found. Please connect a microphone.';
                }
                else if (err.name === 'NotReadableError') {
                    errorMessage = 'Microphone is already in use by another application.';
                }
                else {
                    errorMessage = err.message;
                }
            }
            setStatus(RecordingStatus.ERROR);
            // Call error callback
            onErrorRef.current?.(errorMessage);
        }
    }, [cleanupResources, maxRecordingDuration]);
    /**
     * Safely stops the MediaRecorder if it's active
     */
    const stopMediaRecorder = useCallback(() => {
        if (mediaRecorderRef.current &&
            mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
        }
    }, []);
    const stopRecording = useCallback(() => {
        stopMediaRecorder();
    }, [stopMediaRecorder]);
    const resetRecording = useCallback(() => {
        // Set flag to prevent onstop handler from processing
        isResettingRef.current = true;
        // Stop the recording if active
        stopMediaRecorder();
        // Reset state
        setStatus(RecordingStatus.IDLE);
    }, [stopMediaRecorder]);
    // Cleanup on unmount (handles page refresh, navigation, component removal)
    useEffect(() => {
        return () => {
            // Stop any active recording
            stopMediaRecorder();
            // Release microphone
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
                streamRef.current = null;
            }
            // Note: Parent component is responsible for managing blob URL lifecycle:
            // 1. Create URL: URL.createObjectURL(blob)
            // 2. Store URL in state/global store
            // 3. Revoke when done: URL.revokeObjectURL(url)
        };
    }, [stopMediaRecorder]);
    return {
        status,
        startRecording,
        stopRecording,
        resetRecording,
    };
};
